import { MOVEMENT } from './constants';
import type { PlayerInput } from './protocol';
import { clamp, vec3, vec3Add, vec3Length, vec3Normalize, vec3Scale, type Vec3 } from './math';
import { terrainHeightAt } from './terrain';

export const FALL_DAMAGE_THRESHOLD_VEL = -28;
export const FALL_DAMAGE_SCALE = 0.8;

export function calculateFallDamage(landingVerticalVelocity: number): number {
  return Math.max(0, (-landingVerticalVelocity + FALL_DAMAGE_THRESHOLD_VEL) * FALL_DAMAGE_SCALE);
}

export type JetPhase = 'idle' | 'burst' | 'sustain' | 'recovery' | 'depleted';

export function classifyJetPhase(state: {
  energy: number;
  maxEnergy: number;
  isGrounded: boolean;
  velocity: Vec3;
  previousJetActive?: boolean;
}): JetPhase {
  if (state.isGrounded && !state.previousJetActive) {
    return 'idle';
  }

  if (state.energy <= 0.05 * state.maxEnergy) {
    return 'depleted';
  }

  if (state.previousJetActive && state.energy > 0.7 * state.maxEnergy) {
    return 'burst';
  }

  if (state.previousJetActive && state.energy > 0.2 * state.maxEnergy) {
    return 'sustain';
  }

  if (!state.isGrounded && state.energy < 0.8 * state.maxEnergy) {
    return 'recovery';
  }

  return 'idle';
}

export type MovementTuning = {
  maxEnergy?: number;
  groundAccelerationMultiplier?: number;
  airAccelerationMultiplier?: number;
  jetImpulseMultiplier?: number;
  jetSustainMultiplier?: number;
  jetEmergencyLiftMultiplier?: number;
  jetDrainPerSecond?: number;
  jetRecoveryPerSecond?: number;
  jumpImpulseMultiplier?: number;
  terrainHeightAt?: (x: number, z: number) => number;
};

export type PlayerState = {
  id: string;
  position: Vec3;
  velocity: Vec3;
  yaw: number;
  pitch: number;
  energy: number;
  health: number;
  skiing: boolean;
  isGrounded: boolean;
  speed: number;
  score: number;
  respawnTimer: number;
  isBot: boolean;
  displayName: string;
  wasRecentlyDepleted?: boolean;
  skiCoyoteTimer: number;
  previousJetActive?: boolean;
  kills: number;
  deaths: number;
  captures: number;
  returns: number;
};

export function createDefaultPlayerState(id = 'local'): PlayerState {
  return {
    id,
    position: vec3(0, 12, 0),
    velocity: vec3(0, 0, 0),
    yaw: 0,
    pitch: 0,
    energy: MOVEMENT.maxEnergy,
    health: 100,
    skiing: false,
    isGrounded: false,
    speed: 0,
    score: 0,
    respawnTimer: 0,
    isBot: false,
    displayName: id === 'local' ? 'You' : `Pilot-${id.slice(0, 4)}`,
    kills: 0,
    deaths: 0,
    captures: 0,
    returns: 0,
    skiCoyoteTimer: 0,
    previousJetActive: false,
  };
}

function sampleGroundSlope(position: Vec3, heightAt: (x: number, z: number) => number) {
  const sampleDistance = MOVEMENT.skiSlopeSampleDistance;
  const slopeX = heightAt(position.x + sampleDistance, position.z) - heightAt(position.x - sampleDistance, position.z);
  const slopeZ = heightAt(position.x, position.z + sampleDistance) - heightAt(position.x, position.z - sampleDistance);
  const magnitude = Math.hypot(slopeX, slopeZ);

  if (magnitude === 0) {
    return null;
  }

  return {
    direction: vec3Normalize(vec3(-slopeX, 0, -slopeZ)),
    magnitude,
  };
}

export function simulatePlayer(current: PlayerState, input: PlayerInput, deltaTime: number, tuning: MovementTuning = {}): PlayerState {
  const heightAt = tuning.terrainHeightAt ?? terrainHeightAt;
  const maxEnergy = tuning.maxEnergy ?? MOVEMENT.maxEnergy;
  const isJumpLaunch = input.jump && current.isGrounded;
  const canJet = input.jet && (!current.isGrounded || isJumpLaunch || current.skiCoyoteTimer > 0);
  const hasExplicitJetProfile = tuning.jetDrainPerSecond !== undefined || tuning.jetRecoveryPerSecond !== undefined;
  const energyBias = clamp((maxEnergy - MOVEMENT.maxEnergy) / 40, -1, 1);
  const sustainLiftScale = tuning.jetSustainMultiplier ?? (hasExplicitJetProfile ? 1 : 1 + energyBias * 0.75);

  const forward = vec3(Math.sin(input.yaw), 0, Math.cos(input.yaw));
  const strafe = vec3(forward.z, 0, -forward.x);
  const desiredMove = vec3Normalize(vec3Add(vec3Scale(strafe, input.moveX), vec3Scale(forward, input.moveY)));
  const currentHorizontalVelocity = vec3(current.velocity.x, 0, current.velocity.z);
  const horizontalSpeed = vec3Length(currentHorizontalVelocity);
  const groundSlope = current.isGrounded && current.skiing ? sampleGroundSlope(current.position, heightAt) : null;
  const slopeStrength = groundSlope ? clamp(groundSlope.magnitude / MOVEMENT.skiSlopeAssistScale, 0, 1) : 0;
  const desiredMoveStrength = vec3Length(desiredMove);
  const currentHorizontalDirection = horizontalSpeed > 0.001 ? vec3Normalize(currentHorizontalVelocity) : vec3();
  const carveAlignment =
    current.skiing && current.isGrounded && desiredMoveStrength > 0 && horizontalSpeed > 0.001
      ? clamp(currentHorizontalDirection.x * desiredMove.x + currentHorizontalDirection.z * desiredMove.z, -1, 1)
      : 0;
  const skiCarveSpeedRatio =
    current.skiing && current.isGrounded ? clamp(horizontalSpeed / MOVEMENT.skiCarveSpeedThreshold, 0, 1) : 0;
  const skiCarveAccelerationScale =
    current.skiing && current.isGrounded
      ? 1 -
        skiCarveSpeedRatio +
        skiCarveSpeedRatio *
          (MOVEMENT.skiCarveAccelerationFloor + Math.max(0, carveAlignment) * MOVEMENT.skiCarveAlignedAccelerationBonus)
      : 1;
  const lowSpeedResponse = current.isGrounded
    ? clamp(1 - horizontalSpeed / MOVEMENT.groundLowSpeedResponse, 0, 1) * MOVEMENT.groundLowSpeedAccelerationBoost
    : 0;
  const acceleration = current.isGrounded
    ? MOVEMENT.groundAcceleration *
      (1 + lowSpeedResponse) *
      (current.skiing ? MOVEMENT.skiGroundAccelerationMultiplier : 1) *
      skiCarveAccelerationScale *
      (tuning.groundAccelerationMultiplier ?? 1)
    : MOVEMENT.airAcceleration * (tuning.airAccelerationMultiplier ?? 1);
  const drag = current.isGrounded
    ? current.skiing
      ? MOVEMENT.skiDrag +
        clamp(horizontalSpeed / MOVEMENT.skiCarrySpeed, 0, 1) * MOVEMENT.skiDragAtSpeed +
        clamp((horizontalSpeed - MOVEMENT.skiCarrySpeed) / MOVEMENT.skiCarrySpeed, 0, 1) * MOVEMENT.skiRunawayDrag
      : MOVEMENT.groundDrag
    : MOVEMENT.airDrag;

  let velocity = vec3Add(current.velocity, vec3Scale(desiredMove, acceleration * deltaTime));
  velocity.y -= MOVEMENT.gravity * deltaTime;

  // Phase 51 — Authentic skiing slope acceleration: when skiing on a slope, project
  // gravity along the surface tangent so descents genuinely accelerate the player.
  if (groundSlope && current.skiing && !isJumpLaunch) {
    // Approximate slope angle from sampled height delta (slope magnitude is rise over
    // the sample window).  sin(angle) ~ magnitude / hypot(magnitude, sampleDistance).
    const sampleDistance = MOVEMENT.skiSlopeSampleDistance;
    const sinAngle = groundSlope.magnitude / Math.hypot(groundSlope.magnitude, sampleDistance);
    const tangentAccel = MOVEMENT.gravity * sinAngle;
    velocity = vec3Add(velocity, vec3Scale(groundSlope.direction, tangentAccel * deltaTime));
  }

  // Phase 55 — Slope slide when not skiing: if standing on a steep slope without
  // skis, apply a small downhill slide so steep camping is impossible.
  if (current.isGrounded && !current.skiing && !isJumpLaunch) {
    const standingSlope = sampleGroundSlope(current.position, heightAt);
    if (standingSlope) {
      const sampleDistance = MOVEMENT.skiSlopeSampleDistance;
      const angleDeg = (Math.atan2(standingSlope.magnitude, sampleDistance) * 180) / Math.PI;
      if (angleDeg > MOVEMENT.slopeSlideMinAngleDeg) {
        velocity = vec3Add(velocity, vec3Scale(standingSlope.direction, MOVEMENT.slopeSlideAcceleration * deltaTime));
      }
    }
  }

  // Avoid slope transfer on the exact jump-launch frame to reduce abrupt speed pops when
  // transitioning from ski contact to air.
  if (groundSlope && !isJumpLaunch) {
    const carryBoost = 1 + clamp(horizontalSpeed / MOVEMENT.skiCarrySpeed, 0, MOVEMENT.skiCarryMaxBoost);
    const horizontalDirection = horizontalSpeed > 0.001 ? currentHorizontalDirection : groundSlope.direction;
    const alignment = clamp(horizontalDirection.x * groundSlope.direction.x + horizontalDirection.z * groundSlope.direction.z, -1, 1);
    const downhillAssist = MOVEMENT.gravity * (MOVEMENT.skiDownhillAssist + slopeStrength * 0.2) * carryBoost * deltaTime;
    const uphillPenalty = alignment < 0 ? MOVEMENT.gravity * MOVEMENT.skiUphillPenalty * -alignment * deltaTime : 0;
    const slopeTransfer = clamp(
      downhillAssist * (0.7 + 0.3 * Math.max(0, alignment)) + uphillPenalty,
      0,
      MOVEMENT.skiDownhillTransferCap * deltaTime,
    );
    velocity = vec3Add(velocity, vec3Scale(groundSlope.direction, slopeTransfer));

    if (alignment > 0.1) {
      const projectionBoost = groundSlope.magnitude * 0.12 * clamp(horizontalSpeed / MOVEMENT.skiCarrySpeed, 0, 1) * alignment * deltaTime;
      velocity = vec3Add(velocity, vec3Scale(groundSlope.direction, clamp(projectionBoost, 0, MOVEMENT.skiDownhillTransferCap * 0.3 * deltaTime)));
    }
  }

  let effectiveDrag = drag;
  if (current.skiing && current.isGrounded) {
    const slopeMagnitude = groundSlope ? groundSlope.magnitude : 0;
    if (slopeMagnitude < 0.5) {
      const flatCarryReduction = (1 - slopeMagnitude / 0.5) * 0.18 * clamp(horizontalSpeed / MOVEMENT.skiCarrySpeed, 0, 1);
      effectiveDrag = drag * (1 - flatCarryReduction);
    }
    if (desiredMoveStrength > 0) {
      const hardTurnRatio = clamp((0.25 - carveAlignment) / 1.25, 0, 1);
      effectiveDrag += MOVEMENT.skiCarveHardTurnDrag * hardTurnRatio * skiCarveSpeedRatio;
    }
  }

  velocity = vec3Scale(velocity, Math.max(0, 1 - effectiveDrag * deltaTime));

  const jumpMultiplier = tuning.jumpImpulseMultiplier ?? 1;
  let energy = current.energy;

  const forceAirborne = isJumpLaunch;

  if (forceAirborne) {
    // Jump should be a deterministic impulse independent of pre-jump vertical velocity and frame delta.
    velocity.y = MOVEMENT.jumpImpulse * jumpMultiplier;
  }

  const jetDrainPerSecond = tuning.jetDrainPerSecond ?? MOVEMENT.energyDrainPerSecond * (hasExplicitJetProfile ? 1 : 1 + energyBias * 0.8);
  const jetRecoveryPerSecond = tuning.jetRecoveryPerSecond ?? MOVEMENT.energyRegenPerSecond * (hasExplicitJetProfile ? 1 : 1 - energyBias * 0.25);

  // Phase 59 — Jet ignition burst: small instantaneous upward kick on the frame the
  // player starts jetting (so jet press feels punchy and responsive).
  const isJetIgnition = canJet && !current.previousJetActive && energy >= MOVEMENT.jetIgnitionMinEnergy;
  if (isJetIgnition) {
    velocity.y += MOVEMENT.jetIgnitionBurst;
  }

  if (canJet && energy > 0) {
    const jetLiftMultiplier = clamp(
      MOVEMENT.jetLiftBase - velocity.y * MOVEMENT.jetLiftFalloffPerVerticalSpeed,
      MOVEMENT.jetLiftMinMultiplier,
      MOVEMENT.jetLiftMaxMultiplier,
    );
    const jumpToJetBlend = isJumpLaunch ? MOVEMENT.jumpJetBlend : 1;
    const jumpJetImpulseMultiplier = tuning.jetImpulseMultiplier ?? 1;
    const emergencyLiftMultiplier = tuning.jetEmergencyLiftMultiplier ?? 1;
    const emergencyFallRatio = clamp(
      (-velocity.y - MOVEMENT.jetEmergencyLiftThreshold) / MOVEMENT.jetEmergencyLiftWindow,
      0,
      1,
    );
    const emergencyLiftImpulse = MOVEMENT.jetEmergencyLiftImpulse * emergencyLiftMultiplier * emergencyFallRatio;

    const jetLiftImpulse = MOVEMENT.jetImpulse * jumpJetImpulseMultiplier * jetLiftMultiplier * jumpToJetBlend * sustainLiftScale;
    const pitchVectorFactor = clamp(-input.pitch / MOVEMENT.jetPitchVectorWindow, 0, 1);
    const jetPitchVectorImpulse = jetLiftImpulse * pitchVectorFactor * MOVEMENT.jetPitchVectorHorizontalTransfer;

    velocity.y +=
      (jetLiftImpulse + emergencyLiftImpulse - jetPitchVectorImpulse * MOVEMENT.jetPitchVectorLiftTradeoff) *
      deltaTime;

    const forwardJetIntent = clamp(input.moveY, 0, 1);
    const pitchForwardFactor = clamp(
      1 - Math.abs(input.pitch) / MOVEMENT.jetForwardPitchWindow,
      0,
      1,
    );
    if (forwardJetIntent > MOVEMENT.jetForwardMoveThreshold && pitchForwardFactor > 0) {
      velocity = vec3Add(
        velocity,
        vec3Scale(
          desiredMove,
          (MOVEMENT.jetForwardImpulse * pitchForwardFactor * sustainLiftScale + jetPitchVectorImpulse) *
            forwardJetIntent *
            deltaTime,
        ),
      );
    }

    const panicPenalty = current.wasRecentlyDepleted ? 1.15 : 1;
    energy = clamp(energy - jetDrainPerSecond * panicPenalty * deltaTime, 0, maxEnergy);
  } else {
    // Phase 52 — Energy regen: full rate on the ground, reduced rate airborne so
    // off-jet air-time is not free.  Airborne rate falls back to the same constant
    // when tuning supplies an explicit recovery override.
    const groundedRegen = jetRecoveryPerSecond;
    const airborneRegen = tuning.jetRecoveryPerSecond !== undefined
      ? jetRecoveryPerSecond
      : MOVEMENT.energyAirborneRegenPerSecond;
    const regen = current.isGrounded ? groundedRegen : airborneRegen;
    energy = clamp(energy + regen * deltaTime, 0, maxEnergy);
  }

  if (input.boost && current.skiing && energy > MOVEMENT.skiBoostMinEnergy) {
    const boostDirection =
      desiredMoveStrength > 0
        ? desiredMove
        : horizontalSpeed > 0.001
          ? currentHorizontalDirection
          : forward;
    const speedRatio = clamp(horizontalSpeed / MOVEMENT.skiCarrySpeed, 0, 1);
    const boostImpulse = MOVEMENT.skiBoostImpulse * (0.72 + speedRatio * 0.28) * deltaTime;
    velocity = vec3Add(velocity, vec3Scale(boostDirection, boostImpulse));
    energy = clamp(energy - MOVEMENT.skiBoostEnergyDrainPerSecond * deltaTime, 0, maxEnergy);
  }

  // Phase 60 — Terminal velocity cap: prevent silly through-floor speeds on long
  // falls.  Only clamp downward Y; never touch horizontal momentum.
  if (velocity.y < -MOVEMENT.terminalFallSpeed) {
    velocity.y = -MOVEMENT.terminalFallSpeed;
  }

  const position = vec3Add(current.position, vec3Scale(velocity, deltaTime));
  const groundHeight = heightAt(position.x, position.z) + 2;
  // Keep fast ski scrapes from flickering on shallow bumps without changing the main
  // ski or jet tuning.
  const shallowContactGrace = current.skiing
    ? clamp(horizontalSpeed / MOVEMENT.skiCarrySpeed, 0, 1) *
      clamp(Math.max(0, -velocity.y) / 18, 0, 1) *
      0.35
    : 0;
  const groundSnap = current.skiing
    ? MOVEMENT.skiSurfaceSnap +
      clamp(horizontalSpeed / MOVEMENT.skiSeamSpeedScale, 0, 1) * MOVEMENT.skiSeamForgiveness +
      shallowContactGrace
    : 0;
  const isGrounded = !forceAirborne && position.y <= groundHeight + groundSnap;
  const landedThisFrame = isGrounded && !current.isGrounded && current.skiing;
  const landingVerticalSpeed = landedThisFrame ? Math.max(0, -velocity.y) : 0;

  if (isGrounded) {
    position.y = groundHeight;
    if (landedThisFrame && landingVerticalSpeed > 0) {
      const landingCarry = clamp(landingVerticalSpeed / MOVEMENT.skiLandingCarrySpeed, 0, 1) * MOVEMENT.skiLandingCarry
        + 0.08 * clamp(horizontalSpeed / MOVEMENT.skiCarrySpeed, 0, 1);
      const landingMultiplier = 1 + landingCarry * MOVEMENT.skiLandingCarryMultiplier;
      const landingCarryBoost = clamp(horizontalSpeed / MOVEMENT.skiCarrySpeed, 0, 1) * 0.06 * MOVEMENT.skiLandingCarryMultiplier;
      const totalLandingMultiplier = landingMultiplier + landingCarryBoost;
      velocity.x *= totalLandingMultiplier;
      velocity.z *= totalLandingMultiplier;
    }
    // Phase 57 — Momentum preservation on landing: only zero downward Y on contact;
    // horizontal XZ velocity is preserved so ski-jet chains do not lose speed when
    // skiffing back onto a surface.
    velocity.y = Math.max(0, velocity.y);
  }

  const speed = vec3Length(velocity);

  const wasRecentlyDepleted = energy <= 0.05 * maxEnergy ? true : energy >= maxEnergy ? false : current.wasRecentlyDepleted;

  return {
    ...current,
    position,
    velocity,
    yaw: input.yaw,
    pitch: input.pitch,
    energy,
    speed,
    skiing: current.skiing,
    isGrounded,
    wasRecentlyDepleted,
    // Phase 58 — Ski coyote time: brief grace after leaving ground where a ski toggle
    // still behaves like a ground transition (helps chain ski/jet handoffs).
    skiCoyoteTimer: (current.isGrounded && !isGrounded && current.skiing)
      ? MOVEMENT.skiCoyoteSeconds
      : Math.max(0, (current.skiCoyoteTimer ?? 0) - deltaTime),
    previousJetActive: canJet && energy > 0,
  };
}
